import { httpClient, HttpMethod } from './http-client';
import { UploadedFile } from '../@types/uploaded-files.type';

// Response type for file upload operations
export interface UploadFileItem {
  fileName: string;
  dbSuccess: boolean;
  key?: string;
  id?: number;
  isDeviceImage?: boolean;
}

export interface UploadFilesResponse {
  [key: string]: UploadFileItem;
}

// Response type for presigned URL
export interface PresignedUrlResponse {
  url: string;
  fileName: string;
}

// Response type for multipart upload initialization
export interface MultipartInitResponse {
  uploadId: string;
  key: string;
}

// Response type for multipart URL
export interface MultipartUrlResponse {
  url: string;
}

export const createUploadedFiles = (fileList: FormData): Promise<UploadFilesResponse> => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  return httpClient<UploadFilesResponse>('/uploadedFiles', HttpMethod.Post, fileList, headers);
};

// Export info type
export interface ExportInfo {
  fileType?: string;
  rowCount?: number;
  structureTypeCategory?: string;
  filter: {
    items?: Array<{
      field?: string;
      operator?: string;
      value?: unknown;
    }>;
  };
  startDate?: string;
  endDate?: string;
  locationId?: number;
  includeChilds?: boolean;
  excludedColumns?: string;
}

// Export response type
export interface ExportResponse {
  url: string;
  fileName?: string;
}

export const exportFiles = (exportInfo: ExportInfo): Promise<ExportResponse> => {
  let url = `/export?fileType=${exportInfo?.fileType}`;

  if (exportInfo?.rowCount) {
    url += `&rowCount=${exportInfo?.rowCount}`;
  }

  if (exportInfo?.structureTypeCategory) {
    url += `&structureTypeCategory=${exportInfo?.structureTypeCategory}`;
  }

  exportInfo?.filter.items?.forEach((filter) => {
    url += `&${filter?.field}=${filter?.operator},${filter.value}`;
  });

  if (exportInfo?.startDate) {
    url += `&startDate=${exportInfo?.startDate}`;
  }

  if (exportInfo?.endDate) {
    url += `&endDate=${exportInfo?.endDate}`;
  }

  if (exportInfo?.locationId !== undefined) {
    url += `&locationId=${exportInfo?.locationId}`;
  }

  if (exportInfo?.includeChilds !== undefined) {
    url += `&includeChilds=${exportInfo?.includeChilds}`;
  }

  if (exportInfo?.excludedColumns) {
    url += `&excludedColumns=${exportInfo?.excludedColumns}`;
  }

  return httpClient<ExportResponse>(url, HttpMethod.Post, exportInfo);
};

export const deleteUploadedFile = (id: number | string): Promise<void> => {
  return httpClient<void>(`/uploadedFiles/delete/${id}`, HttpMethod.Put, null);
};

export const uploadFiles = (
  entityId: number,
  fileList: any,
  entityType: string,
  featureType?: string
): Promise<UploadFilesResponse> => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  let url = `/uploadedFiles/${entityId}/entityType/${entityType}`;
  if (featureType) {
    url += `?featureType=${featureType}`;
  }
  return httpClient<UploadFilesResponse>(url, HttpMethod.Post, fileList, headers, 30000);
};

export const getUploadedFiles = (
  entityId: number,
  entityType: string,
  notIncludeUrlsByDefault?: boolean
): any => {
  return httpClient(
    `/uploadedFiles/${entityId}/entityType/${entityType}?notIncludeUrlsByDefault=${notIncludeUrlsByDefault}`,
    HttpMethod.Get,
    null
  );
};

export const permanantDeleteUploadedFile = (id: number): Promise<void> => {
  return httpClient<void>(`/uploadedFiles/${id}`, HttpMethod.Delete, null);
};

export const updateUploadedFileData = (id: number, isMain: boolean): Promise<void> => {
  return httpClient<void>(`/uploadedFile/data/${id}`, HttpMethod.Put, { isMain });
};

// Response for CSV file info
export interface UploadedCsvFileResponse {
  url: string;
  fileName: string;
}

export const getUploadedCsvFile = (key: string): Promise<UploadedCsvFileResponse> => {
  return httpClient<UploadedCsvFileResponse>(`/uploadedFiles/key/${encodeURIComponent(key)}`, HttpMethod.Get);
};

export const getUploadedFilePresignedUrlById = (id: number, contentDispositionType?: string): Promise<PresignedUrlResponse> => {
  return httpClient<PresignedUrlResponse>(
    `/uploadedFiles/presignedUrl/${id}?contentDispositionType=${contentDispositionType}`,
    HttpMethod.Get
  );
};

export const getUploadedFilePresignedUrlByKey = (
  key: string,
  mimeType?: string,
  contentDispositionType?: string
): Promise<PresignedUrlResponse> => {
  return httpClient<PresignedUrlResponse>(
    `/uploadedFiles/presignedUrlKey/${encodeURIComponent(key)}?mimeType=${mimeType}&contentDispositionType=${contentDispositionType}`,
    HttpMethod.Get
  );
};

export interface ManifestExportResponse {
  url: string;
  fileName?: string;
}

export const exportDispatchManifest = (shipmentId: number): Promise<ManifestExportResponse> => {
  return httpClient<ManifestExportResponse>(`/manifestExport/${shipmentId}`, HttpMethod.Post, {});
};

// Response type for user profile image upload
export interface ProfileImageUploadResponse {
  url: string;
  fileName?: string;
  key?: string;
}

export const uploadUserProfileImage = (entityId: number, fileList: FormData): Promise<ProfileImageUploadResponse[]> => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  return httpClient(
    `/uploadedFiles/user/${entityId}/profile?entityType=userProfiles`,
    HttpMethod.Post,
    fileList,
    headers
  );
};

// Multipart upload types
export interface MultipartPartData {
  ETag: string | null;
  PartNumber: number;
}

export interface MultipartCompleteResponse {
  id?: number;
  key: string;
  fileName: string;
}

export const initializeUploadedFilesMultipart = (
  fileName: string,
  entityId: number,
  entityType: string
): Promise<MultipartInitResponse> => {
  return httpClient<MultipartInitResponse>('/initializeUploadedFilesMultipart', HttpMethod.Post, {
    fileName,
    entityId,
    entityType,
  });
};

export const getUploadedFilesMultipartUrl = (
  fileName: string,
  partNumber: number,
  uploadId: string
): Promise<MultipartUrlResponse> => {
  return httpClient<MultipartUrlResponse>('/getUploadedFilesMultipartUrl', HttpMethod.Post, {
    fileName,
    partNumber,
    uploadId,
  });
};

export const completeUploadedFilesMultipart = (
  key: string,
  uploadId: string,
  parts: MultipartPartData[],
  originalFileName: string,
  entityId: number,
  entityType: string,
  mimeType: string
): Promise<MultipartCompleteResponse> => {
  return httpClient<MultipartCompleteResponse>('/completeUploadedFilesMultipart', HttpMethod.Post, {
    key,
    uploadId,
    parts,
    originalFileName,
    entityId,
    entityType,
    mimeType,
  });
};

export const abortUploadedFilesMultipartUpload = (key: string, uploadId: string): Promise<void> => {
  return httpClient<void>('/abortUploadedFilesMultipartUpload', HttpMethod.Post, { key, uploadId });
};
